import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { ContactRow } from "./ContactBits";

// Photo header — banner with photo on top, single column below.
export default function PhotoHeaderTemplate({ resume }) {
  const { photo, personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#0a0a0a", background: "#ffffff", width: "100%", height: "100%", boxSizing: "border-box" }}>
      {/* Banner — full-bleed coloured band. Keeps its intentionally tight
          32/48 px padding so the photo + name + contact row hug the band
          as designed. Horizontal padding is bumped to 2 cm so header
          text still respects the page-margin contract even though the
          coloured background bleeds to the paper edge. */}
      <div style={{ background: "#F7F7F8", padding: "32px 2cm", borderBottom: "3px solid #2400FF", display: "flex", alignItems: "center", gap: 20 }}>
        {photo && (
          <div style={{ width: 84, height: 84, borderRadius: 6, overflow: "hidden", border: "2px solid #ffffff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
            <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div data-resume-h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: "-0.02em", lineHeight: 1 }}>{personal.fullName || "Your Name"}</div>
          {personal.title && <div style={{ fontSize: 12, color: "#2400FF", fontWeight: 600, marginTop: 3 }}>{personal.title}</div>}
          <ContactRow
            personal={personal}
            size={10}
            color="#525252"
            separator="·"
            style={{ marginTop: 6, fontSize: 9.5, gap: 8 }}
          />
        </div>
      </div>

      {/* Body — 2 cm padding on all sides gives the text content the
          required safe area below the full-bleed banner. */}
      <div style={{ padding: "2cm", fontSize: 10, lineHeight: 1.5 }}>
        {summary && <Sec title="Professional Summary"><div>{summary}</div></Sec>}

        {skills?.length > 0 && (
          <Sec title="Skills">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {skills.map((s, i) => <span key={`${s}-${i}`} style={{ background: "#F7F7F8", padding: "3px 8px", fontSize: 9.5, border: "1px solid rgba(0,0,0,0.1)" }}>{s}</span>)}
            </div>
          </Sec>
        )}

        {hasAny(experience, ["company", "role"]) && (
          <Sec title="Experience">
            {experience.map((e, i) => {
              const bullets = nonEmptyBullets(e.bullets);
              if (!e.company && !e.role && bullets.length === 0) return null;
              return (
                <div key={e.id || i} data-page-item="true" style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div><b data-resume-jobtitle>{e.role}</b><span style={{ color: "#525252" }}> · <span data-resume-company>{e.company}</span></span></div>
                    <div style={{ fontSize: 9, color: "#737373", fontFamily: "'IBM Plex Mono', monospace" }}><span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span>{e.location ? <> · <span data-resume-location>{e.location}</span></> : ""}</div>
                  </div>
                  {bullets.length > 0 && (
                    <ul data-resume-workdesc style={{ margin: "3px 0 0 16px", padding: 0 }}>
                      {bullets.map((b, bi) => <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2 }}>{b}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </Sec>
        )}

        {hasAny(education, ["school", "degree"]) && (
          <Sec title="Education">
            {education.map((ed, i) => (ed.school || ed.degree) && (
              <div key={ed.id || i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <div><b>{ed.school}</b><span style={{ color: "#525252" }}> · {ed.degree}{ed.field ? `, ${ed.field}` : ""}</span></div>
                <div style={{ fontSize: 9, color: "#737373" }}>{fmtRange(ed.startDate, ed.endDate)}</div>
              </div>
            ))}
          </Sec>
        )}

        {hasAny(projects, ["name"]) && (
          <Sec title="Projects">
            {projects.map((p, i) => p.name && (
              <div key={p.id || i} style={{ marginBottom: 5 }}>
                <b data-resume-projectname>{p.name}</b>{p.tech ? <span style={{ color: "#737373" }}> · {p.tech}</span> : null}
                {p.description && <div>{p.description}</div>}
              </div>
            ))}
          </Sec>
        )}

        {hasAny(certifications, ["name"]) && (
          <Sec title="Certifications">
            {certifications.map((c, i) => c.name && (
              <div key={c.id || i}><b>{c.name}</b><span style={{ color: "#525252" }}> · {c.issuer}</span>{c.date ? <span style={{ color: "#a3a3a3" }}> · {c.date}</span> : null}</div>
            ))}
          </Sec>
        )}

        {languages?.some((l) => l.name?.trim()) && (
          <Sec title="Languages">
            {languages.map((l) => l.name?.trim() && (
              <div key={l.id}><b>{l.name}</b>{l.level ? <span style={{ color: "#525252" }}> · {l.level}</span> : null}</div>
            ))}
          </Sec>
        )}
      </div>
    </div>
  );
}

function Sec({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 12 }}>
      <div data-resume-h2 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: "#2400FF", marginBottom: 5 }}>{title}</div>
      {children}
    </section>
  );
}
